import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Lock, Menu, X } from 'lucide-react';

/** Monochrome mark · `public/fidus-finance-symbol.png` */
const BRAND_SYMBOL_SRC = '/fidus-finance-symbol.png';

/** Served from `public/hero.mp4` — bundled asset deployed with the site. */
const HERO_VIDEO_SRC = '/hero.mp4';
const HERO_POSTER =
  'https://images.unsplash.com/photo-1513828583688-c52646db42da?auto=format&fit=crop&w=1920&q=80';

export interface HeaderRefs {
  about: RefObject<HTMLElement>;
  services: RefObject<HTMLElement>;
  locations: RefObject<HTMLElement>;
  security: RefObject<HTMLElement>;
  contact: RefObject<HTMLElement>;
}

interface HeaderProps {
  onNavigate: (ref: RefObject<HTMLElement>) => void;
  refs: HeaderRefs;
}

function clearScrollLock() {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  document.body.style.overflow = '';
  document.documentElement.style.overflow = '';
}

export default function Header({ onNavigate, refs }: HeaderProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);
  const lockedScrollY = useRef<number | null>(null);

  const menuItems: { key: keyof HeaderRefs; label: string }[] = [
    { key: 'about', label: 'About' },
    { key: 'services', label: 'Solutions' },
    { key: 'locations', label: 'Office' },
    { key: 'security', label: 'Security' },
    { key: 'contact', label: 'Contact' },
  ];

  const releaseScrollLock = () => {
    const y = lockedScrollY.current;
    if (y === null) return;
    lockedScrollY.current = null;
    clearScrollLock();
    window.scrollTo(0, y);
  };

  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () => setHeaderHeight(el.getBoundingClientRect().height);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const closeOnDesktop = () => {
      if (mq.matches) setMobileOpen(false);
    };
    mq.addEventListener('change', closeOnDesktop);
    return () => mq.removeEventListener('change', closeOnDesktop);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;

    const y = window.scrollY;
    lockedScrollY.current = y;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${y}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    const blockBackgroundScroll = (event: TouchEvent) => {
      const nav = document.getElementById('site-mobile-nav');
      if (nav?.contains(event.target as Node)) return;
      event.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('touchmove', blockBackgroundScroll, { passive: false });

    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('touchmove', blockBackgroundScroll);
      const restore = lockedScrollY.current;
      lockedScrollY.current = null;
      clearScrollLock();
      if (restore !== null) window.scrollTo(0, restore);
    };
  }, [mobileOpen]);

  const go = (ref: RefObject<HTMLElement>) => {
    releaseScrollLock();
    setMobileOpen(false);
    onNavigate(ref);
  };

  const mobileMenu =
    mobileOpen && typeof document !== 'undefined'
      ? createPortal(
          <nav
            id="site-mobile-nav"
            aria-label="Mobile"
            className="fixed inset-x-0 z-[70] flex w-full flex-col items-stretch justify-start overflow-y-auto overscroll-contain md:hidden"
            style={{
              top: headerHeight,
              backgroundColor: '#070b12',
              opacity: 1,
              backdropFilter: 'none',
              WebkitBackdropFilter: 'none',
            }}
          >
            <div className="flex items-center justify-between border-b border-[#e8ede4]/15 px-5 py-2">
              <span className="text-xs font-semibold uppercase tracking-[0.28em] text-[#f1f5ec]">
                Menu
              </span>
              <button
                type="button"
                className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#e8ede4]/25 text-[#f1f5ec] transition hover:border-teal-200/45"
                onClick={() => setMobileOpen(false)}
                aria-label="Close navigation"
              >
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <div className="flex flex-col">
              {menuItems.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => go(refs[key])}
                  className="flex min-h-11 items-center border-b border-[#e8ede4]/10 px-5 text-left text-sm font-semibold uppercase tracking-[0.18em] text-[#f1f5ec] transition hover:bg-white/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-teal-200"
                >
                  {label}
                </button>
              ))}
            </div>
          </nav>,
          document.body,
        )
      : null;

  return (
    <>
      <header
        ref={headerRef}
        className={`fixed inset-x-0 top-0 z-50 border-b border-[#e8ede4]/10 ${
          mobileOpen
            ? 'bg-[#070b12] backdrop-blur-none md:bg-[#070b12]/95 md:backdrop-blur-xl'
            : 'bg-[#070b12]/95 backdrop-blur-xl'
        }`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-5 py-4 md:px-8">
          <button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex items-center gap-2 text-left transition hover:opacity-90"
            aria-label="Fidus Finance home"
          >
            <img
              src={BRAND_SYMBOL_SRC}
              alt=""
              width={32}
              height={32}
              decoding="async"
              className="h-8 w-8 shrink-0 object-contain opacity-[0.94]"
              aria-hidden
            />
            <span className="text-lg font-semibold tracking-tight text-[#f1f5ec]">
              FIDUS <span className="font-normal text-[#e8ede4]/85">FINANCE</span>
            </span>
          </button>

          <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
            {menuItems.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => go(refs[key])}
                className="text-xs font-medium uppercase tracking-[0.22em] text-[#e8ede4]/90 transition hover:text-white"
              >
                {label}
              </button>
            ))}
          </nav>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#e8ede4]/15 text-[#f1f5ec] transition hover:border-teal-200/45 md:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-expanded={mobileOpen}
            aria-controls="site-mobile-nav"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>
      {mobileMenu}

      <section className="relative isolate min-h-[100svh] overflow-hidden pt-24 md:pt-[5.5rem]">
        <div className="pointer-events-none absolute inset-0 z-0">
          {!videoFailed ? (
            <video
              className="h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              poster={HERO_POSTER}
              aria-hidden
              onError={() => setVideoFailed(true)}
            >
              <source src={HERO_VIDEO_SRC} type="video/mp4" />
            </video>
          ) : (
            <img
              src={HERO_POSTER}
              alt=""
              className="h-full w-full object-cover"
              fetchPriority="high"
            />
          )}
          <div
            className="absolute inset-0 bg-[linear-gradient(180deg,rgba(7,11,18,0.9)_0%,rgba(7,11,18,0.78)_40%,rgba(7,11,18,0.92)_100%)]"
            aria-hidden
          />
          <div
            className="absolute inset-0 bg-[linear-gradient(105deg,rgba(7,11,18,0.96)_0%,rgba(7,11,18,0.86)_42%,rgba(7,11,18,0.55)_72%,rgba(7,11,18,0.34)_100%)]"
            aria-hidden
          />
          {/* Teal radial accent */}
          <div
            className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_18%,rgba(45,212,191,0.12),transparent_58%)]"
            aria-hidden
          />
          <div
            className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#070b12] to-transparent"
            aria-hidden
          />
        </div>

        <div className="relative z-10 mx-auto flex min-h-[calc(100svh-5rem)] max-w-7xl flex-col justify-center px-5 pb-16 pt-8 md:min-h-[calc(100svh-4.75rem)] md:px-8 md:pb-24 md:pt-10">
          <div className="hero-text relative max-w-4xl">
            <div
              aria-hidden
              className="absolute -inset-x-4 -inset-y-7 -z-10 rounded-[1.75rem] border border-[#e8ede4]/20 bg-[#070b12]/95 shadow-[0_30px_90px_-36px_rgba(0,0,0,0.95)] backdrop-blur-xl sm:-inset-x-7 sm:-inset-y-8 md:-inset-x-9"
            />
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#d6ddcf]/45 bg-[#070b12]/80 px-4 py-2">
              <Lock className="h-4 w-4 text-teal-200" aria-hidden />
              <span className="text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-[#e4ebe0]">
                Brazilian energy software
              </span>
            </div>
            <h1 className="max-w-4xl text-5xl font-semibold leading-[0.98] tracking-[-0.055em] text-[#f1f5ec] sm:text-6xl lg:text-7xl">
              Secure infrastructure for{' '}
              <span className="text-teal-100">sustainable energy</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-8 text-[#e4ebe2]">
              Fidus Finance is a Brazilian software company headquartered in Florianópolis. We build
              the internal platforms energy operators rely on — plant telemetry, dispatch and
              operational data — with blockchain-verified records.
            </p>
            <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              <button
                type="button"
                onClick={() => go(refs.services)}
                className="hover-scale inline-flex items-center justify-center rounded-full bg-[#e8ede4] px-6 py-3 text-sm font-semibold text-[#07101d] shadow-lg shadow-black/25 transition hover:bg-teal-100"
              >
                Explore solutions
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => go(refs.about)}
                className="inline-flex items-center justify-center rounded-full border-2 border-[#d8e0d1] bg-[#121a26] px-6 py-3 text-sm font-semibold text-[#f1f5ec] shadow-[0_10px_28px_rgba(0,0,0,0.45)] transition hover:border-teal-100 hover:bg-[#1c2633]"
              >
                Learn more
              </button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
